import { useCallback } from "react";
import { logOfficeError } from "../../../telemetry";
import { emitToast } from "../../../../integrations/tools/outlook/toast-bridge";

/**
 * Returns a click handler that opens a specific email message in Outlook.
 *
 * Strategy (most → least native), mirroring useOpenCalendarEvent:
 *  1. displayMessageForm        (classic Outlook desktop / web)
 *  2. openBrowserWindow webLink (Req Set 1.2+, New Outlook / OWA)
 *  3. window.open webLink       (any host with a browser context)
 *
 * The Office.js call is not available on every host, and it reports no
 * failure callback, so without the webLink fallback a click on a search
 * result is a silent no-op. When there is no link to fall back to either,
 * say so with a toast rather than doing nothing.
 */
export function useOpenMessage(): (itemId: string, webLink?: string) => void {
  return useCallback((itemId: string, webLink?: string) => {
    try {
      if (typeof Office.context.mailbox?.displayMessageForm === "function") {
        Office.context.mailbox.displayMessageForm(itemId);
        return;
      }
    } catch (err) {
      console.warn("[useOpenMessage] Failed to open message:", err);
      logOfficeError(err, "outlook-open-message");
    }

    if (!webLink) {
      emitToast({
        title: "Can't open this email here. Open it from your Outlook mailbox.",
        intent: "error",
      });
      return;
    }

    try {
      if (typeof Office.context.ui?.openBrowserWindow === "function") {
        Office.context.ui.openBrowserWindow(webLink);
        return;
      }
    } catch (err) {
      console.warn("[useOpenMessage] Failed to open browser window:", err);
      logOfficeError(err, "outlook-open-message");
    }

    window.open(webLink, "_blank");
  }, []);
}
